import { Localized } from "@fluent/react"
import { Box, Checkbox, CircularProgress, FormControlLabel, FormHelperText, Stack, TextField, Typography, debounce } from "@mui/material"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { RichTextContainer, tiptapExtensions } from "../../../../app/RichTextEditor"
import { isDocEditable } from "../../documentSlice"
import { showError } from "../../../notifications/notificationsSlice"
import { loadRiskAnalysisReviewSummary, RiskAnalysisReviewClassificationEnum, RiskAnalysisSummaryDto, upsertRiskAnalysisReviewSummary } from "../riskAnalysisApi"
import { useEditor } from "@tiptap/react"

interface RiskAnalysisSummarySectionProps {
	documentId: number
}

const reviewClassificationOrder: RiskAnalysisReviewClassificationEnum[] = [
	RiskAnalysisReviewClassificationEnum.ReviewNotRequired,
	RiskAnalysisReviewClassificationEnum.AtLeastOneReviewRequired,
	RiskAnalysisReviewClassificationEnum.PeriodicReviewRequired,
	RiskAnalysisReviewClassificationEnum.NotApplicable,
]

const reviewClassificationLabelMap: Record<RiskAnalysisReviewClassificationEnum, string> = {
	[RiskAnalysisReviewClassificationEnum.ReviewNotRequired]: "risk-analysis-review-summary-no-review",
	[RiskAnalysisReviewClassificationEnum.AtLeastOneReviewRequired]: "risk-analysis-review-summary-at-least-one-review",
	[RiskAnalysisReviewClassificationEnum.PeriodicReviewRequired]: "risk-analysis-review-summary-periodic-review",
	[RiskAnalysisReviewClassificationEnum.NotApplicable]: "risk-analysis-review-summary-not-applicable",
}

const createEmptySummary = (documentId: number): RiskAnalysisSummaryDto => ({
	id: 0,
	documentId,
	sectionId: null,
	reviewClassification: RiskAnalysisReviewClassificationEnum.NotApplicable,
	scheduledReviewDate: null,
	justification: "",
})

const normalizeScheduledReviewDate = (value: string | null | undefined) => {
	if (!value) {
		return null
	}

	return value.split("T")[0]
}

const normalizeSummary = (summary: RiskAnalysisSummaryDto): RiskAnalysisSummaryDto => ({
	...summary,
	scheduledReviewDate: normalizeScheduledReviewDate(summary.scheduledReviewDate),
})

const normalizeClassificationString = (value: string) => value.toUpperCase().replace(/[^A-Z]/g, "")

const fromApiClassification = (value: RiskAnalysisReviewClassificationEnum | string | null | undefined): RiskAnalysisReviewClassificationEnum | null => {
	if (value === null || value === undefined || value === "") {
		return null
	}

	if (typeof value === "number") {
		switch (value) {
			case 0:
				return RiskAnalysisReviewClassificationEnum.NotApplicable
			case 1:
				return RiskAnalysisReviewClassificationEnum.ReviewNotRequired
			case 2:
				return RiskAnalysisReviewClassificationEnum.AtLeastOneReviewRequired
			case 3:
				return RiskAnalysisReviewClassificationEnum.PeriodicReviewRequired
			default:
				return null
		}
	}

	const normalized = normalizeClassificationString(String(value))

	if (normalized.includes("PERIODIC")) {
		return RiskAnalysisReviewClassificationEnum.PeriodicReviewRequired
	}

	if (normalized.includes("LEAST") || normalized.includes("ONEREVIEW") || normalized.includes("ONETIME")) {
		return RiskAnalysisReviewClassificationEnum.AtLeastOneReviewRequired
	}

	if (normalized.includes("NOTAPPLICABLE") || normalized === "NA" || normalized === "ND") {
		return RiskAnalysisReviewClassificationEnum.NotApplicable
	}

	if (normalized.includes("NOREVIEW") || normalized.includes("DOESNOTREQUIRE") || normalized.includes("NOTREQUIRED")) {
		return RiskAnalysisReviewClassificationEnum.ReviewNotRequired
	}

	return null
}

const requiresScheduledDate = (classification: RiskAnalysisReviewClassificationEnum | null) =>
	classification === RiskAnalysisReviewClassificationEnum.AtLeastOneReviewRequired
	|| classification === RiskAnalysisReviewClassificationEnum.PeriodicReviewRequired

const ReviewClassificationItem = (props: {
	classification: RiskAnalysisReviewClassificationEnum
	selected: boolean
	disabled: boolean
	onSelect: (classification: RiskAnalysisReviewClassificationEnum) => void
}) => {
	const { classification, selected, disabled, onSelect } = props

	return (
		<Box
			onClick={() => !disabled && onSelect(classification)}
			sx={{
				borderRadius: 1,
				px: 1,
				py: 0.25,
				cursor: disabled ? "default" : "pointer",
				backgroundColor: selected ? "action.selected" : "transparent",
				display: "flex",
				alignItems: "flex-start",
			}}
		>
			<FormControlLabel
				sx={{
					alignItems: "center",
					m: 0,
					width: "100%",
					'& .MuiFormControlLabel-label': {
						lineHeight: 1.4,
					},
				}}
				control={<Checkbox checked={selected} disabled={disabled} onChange={() => onSelect(classification)} />}
				label={
					<Typography variant="body2" sx={{ fontWeight: selected ? 700 : 500 }}>
						<Localized id={reviewClassificationLabelMap[classification]}>{classification}</Localized>
					</Typography>
				}
			/>
		</Box>
	)
}

export const RiskAnalysisSummarySectionView = ({ documentId }: RiskAnalysisSummarySectionProps) => {
	const dispatch = useAppDispatch()
	const isEditable = useAppSelector(isDocEditable)

	const [summary, setSummary] = useState<RiskAnalysisSummaryDto>(() => createEmptySummary(documentId))
	const [classification, setClassification] = useState<RiskAnalysisReviewClassificationEnum | null>(null)
	const [loading, setLoading] = useState(false)
	const [showValidation, setShowValidation] = useState(false)
	const hydratingRef = useRef(false)
	const initialLoadDoneRef = useRef(false)

	const autosave = useCallback(debounce((nextSummary: RiskAnalysisSummaryDto, nextClassification: RiskAnalysisReviewClassificationEnum | null) => {
		if (!isEditable || nextClassification === null) {
			return
		}

		dispatch(upsertRiskAnalysisReviewSummary({
			documentId,
			summary: {
				...nextSummary,
				documentId,
				reviewClassification: nextClassification,
				scheduledReviewDate: requiresScheduledDate(nextClassification) ? nextSummary.scheduledReviewDate : null,
			},
		}))
			.unwrap()
			.then(saved => {
				hydratingRef.current = true
				setSummary(normalizeSummary(saved))
				setClassification(fromApiClassification(saved.reviewClassification))
			})
			.catch(() => {
				dispatch(showError("error"))
			})
	}, 300), [dispatch, documentId, isEditable])

	const justificationEditor = useEditor({
		extensions: tiptapExtensions,
		content: "",
		editable: isEditable,
		onUpdate: e => {
			if (hydratingRef.current) {
				hydratingRef.current = false
				return
			}

			setSummary(current => {
				const nextSummary = { ...current, justification: e.editor.getHTML() }
				if (initialLoadDoneRef.current) {
					autosave(nextSummary, classification)
				}
				return nextSummary
			})
		},
	})

	useEffect(() => {
		const load = async () => {
			setLoading(true)
			try {
				const result = await dispatch(loadRiskAnalysisReviewSummary({ documentId })).unwrap()
				const current = normalizeSummary(result[0] ?? createEmptySummary(documentId))
				const currentClassification = fromApiClassification(current.reviewClassification)
				hydratingRef.current = true
				setSummary(current)
				setClassification(currentClassification)
				initialLoadDoneRef.current = true
			} catch {
				dispatch(showError("error"))
			} finally {
				setLoading(false)
			}
		}

		load()
	}, [dispatch, documentId])

	useEffect(() => {
		if (justificationEditor && justificationEditor.getHTML() !== summary.justification) {
			hydratingRef.current = true
			justificationEditor.commands.setContent(summary.justification || "")
		}
	}, [justificationEditor, summary.justification])

	useEffect(() => {
		if (justificationEditor) {
			justificationEditor.setEditable(isEditable)
		}
	}, [justificationEditor, isEditable])

	useEffect(() => () => {
		autosave.clear()
	}, [autosave])

	const dateEnabled = useMemo(() => requiresScheduledDate(classification), [classification])

	const handleClassificationChange = (nextClassification: RiskAnalysisReviewClassificationEnum) => {
		setShowValidation(true)
		setClassification(nextClassification)
		setSummary(current => {
			const nextSummary = {
				...current,
				scheduledReviewDate: requiresScheduledDate(nextClassification) ? current.scheduledReviewDate : null,
			}
			autosave(nextSummary, nextClassification)
			return nextSummary
		})
	}

	const handleScheduledReviewDateChange = (value: string) => {
		setSummary(current => {
			const nextSummary = { ...current, scheduledReviewDate: value || null }
			autosave(nextSummary, classification)
			return nextSummary
		})
	}

	return (
		<Box sx={{ px: 1, pb: 2 }}>
			<Stack spacing={2.5}>
				{loading ? (
					<Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
						<CircularProgress size={24} />
					</Box>
				) : (
					<>
						<Box
							sx={{
								maxWidth: 720,
								border: "1px solid",
								borderColor: "divider",
								borderRadius: 1,
								px: 2,
								pt: 2.5,
								pb: 1.5,
								position: "relative",
							}}
						>
							<Box
								sx={{
									position: "absolute",
									top: -10,
									left: 12,
									px: 1,
									backgroundColor: "background.paper",
								}}
							>
								<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
									<Localized id="note">Ocena</Localized>
								</Typography>
							</Box>

							<Stack sx={{ gap: 0.5 }}>
								{reviewClassificationOrder.map(option => (
									<ReviewClassificationItem
										key={option}
										classification={option}
										selected={classification === option}
										disabled={!isEditable}
										onSelect={handleClassificationChange}
									/>
								))}
							</Stack>
						</Box>

						{showValidation && classification === null && (
							<FormHelperText error>
								<Localized id="risk-analysis-review-summary-select-classification">Select one review classification.</Localized>
							</FormHelperText>
						)}

						<Box sx={{ width: "100%", maxWidth: 320 }}>
							<TextField
								label={<Localized id="risk-analysis-review-summary-date">Scheduled review date</Localized>}
								type="date"
								value={summary.scheduledReviewDate ?? ""}
								onChange={e => handleScheduledReviewDateChange(e.target.value)}
								disabled={!isEditable || !dateEnabled}
								fullWidth
								size="small"
								InputLabelProps={{ shrink: true }}
							/>
						</Box>

						<Box
							sx={{
								width: "100%",
								maxWidth: 960,
								'& .tiptap': {
									minHeight: 50,
									maxHeight: 120,
									overflowY: 'auto',
								},
							}}
						>
							<Typography variant="subtitle2" sx={{ mb: 1 }}>
								<Localized id="risk-analysis-review-summary-justification">Justification for choice</Localized>
							</Typography>
							<RichTextContainer label={undefined} editor={justificationEditor} />
						</Box>
					</>
				)}
			</Stack>
		</Box>
	)
}
